import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, FileText, Package } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const FONTS = [
  ["Montserrat Black", "Logo wordmark only", "Montserrat", 900, "MOVETENDER"],
  ["Outfit Bold", "Headings and ad headlines", "Outfit", 700, "Your move, best price."],
  ["Plus Jakarta Sans", "Body text, buttons, emails", "Plus Jakarta Sans", 400, "Post your move for free and removalists compete."],
  ["JetBrains Mono", "Small labels and figures", "JetBrains Mono", 500, "26.1 KM · 42 M³ · 3 BIDS"],
];

const fetchBlob = async (file) => (await api.get(`/admin/brand/files/${file}`, { responseType: "blob" })).data;

async function download(file) {
  try {
    const url = URL.createObjectURL(await fetchBlob(file));
    const a = Object.assign(document.createElement("a"), { href: url, download: file });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    toast.error("Download failed, please try again");
  }
}

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${text}`)).catch(() => toast.error("Couldn't copy"));

function LogoCard({ a }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    fetchBlob(a.file).then((b) => { url = URL.createObjectURL(b); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [a.file]);
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-white" data-testid={`brand-asset-${a.file}`}>
      <div className="h-32 flex items-center justify-center p-5" style={{ background: a.bg }}>
        {src && <img src={src} alt={a.label} className="max-h-full max-w-full object-contain" />}
      </div>
      <div className="p-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{a.label}</p>
          <p className="text-xs text-muted-foreground">{a.use}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => download(a.file)} data-testid={`brand-download-${a.file}`} aria-label={`Download ${a.label}`}>
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function ColourCard({ c }) {
  const light = ["#FDF2E9", "#FFFFFF"].includes(c.hex);
  const codes = [["HEX", c.hex], ["RGB", c.rgb.join(", ")], ["CMYK", c.cmyk.join(", ")]];
  return (
    <div className="rounded-xl border border-border overflow-hidden" data-testid={`brand-colour-${c.hex.slice(1)}`}>
      <div className={`h-20 p-3 font-display font-bold ${light ? "text-foreground" : "text-white"}`} style={{ background: c.hex }}>{c.name}</div>
      <div className="p-3 space-y-1">
        {codes.map(([k, v]) => (
          <button key={k} type="button" onClick={() => copy(v)} className="w-full flex items-center justify-between text-xs font-mono hover:text-kinetic" data-testid={`brand-copy-${c.hex.slice(1)}-${k.toLowerCase()}`}>
            <span className="text-muted-foreground">{k}</span><span className="flex items-center gap-1">{v}<Copy className="h-3 w-3" /></span>
          </button>
        ))}
        <p className="text-xs text-muted-foreground pt-1">{c.use}</p>
      </div>
    </div>
  );
}

function SocialCard({ t }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    fetchBlob(t.file).then((b) => { url = URL.createObjectURL(b); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [t.file]);
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-white flex flex-col" data-testid={`brand-social-${t.file}`}>
      <div className="h-56 bg-slate-100 flex items-center justify-center p-3">
        {src && <img src={src} alt={t.label} className="max-h-full max-w-full object-contain rounded shadow-sm" />}
      </div>
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">{t.label}</p>
            <p className="text-xs font-mono text-muted-foreground">{t.size[0]} × {t.size[1]} px</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => download(t.file)} data-testid={`brand-download-${t.file}`} aria-label={`Download ${t.label}`}><Download className="h-4 w-4" /></Button>
        </div>
        <p className="text-xs text-muted-foreground flex-1">{t.caption}</p>
        <Button size="sm" variant="ghost" className="self-start px-2 h-7 text-xs" onClick={() => copy(t.caption)} data-testid={`brand-copy-caption-${t.file}`}><Copy className="h-3 w-3 mr-1" />Copy caption</Button>
      </div>
    </div>
  );
}

export function BrandKitTab() {
  const [kit, setKit] = useState(null);
  useEffect(() => { api.get("/admin/brand").then((r) => setKit(r.data)).catch(() => toast.error("Couldn't load the brand kit")); }, []);
  if (!kit) return <p className="text-sm text-muted-foreground" data-testid="brand-loading">Loading brand kit…</p>;
  return (
    <div className="space-y-6" data-testid="brand-kit">
      <Card>
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <p className="font-display text-xl font-bold">MoveTender brand kit</p>
            <p className="text-sm text-muted-foreground">Send these to designers, printers or a video maker so everything looks like MoveTender.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => download("movetender-brand-guide.pdf")} className="bg-kinetic hover:bg-kinetic-hover" data-testid="brand-download-guide"><FileText className="h-4 w-4 mr-2" />Brand guide (PDF)</Button>
            <Button variant="outline" onClick={() => download("movetender-brand-kit.zip")} data-testid="brand-download-zip"><Package className="h-4 w-4 mr-2" />Everything (ZIP)</Button>
            <Button variant="outline" onClick={() => download("colours.txt")} data-testid="brand-download-colours"><Download className="h-4 w-4 mr-2" />Colour codes</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Logo files</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{kit.assets.map((a) => <LogoCard key={a.file} a={a} />)}</CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Social media templates <span className="text-sm font-normal text-muted-foreground">ready to post, with suggested captions</span></CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{(kit.social || []).map((t) => <SocialCard key={t.file} t={t} />)}</CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Colours <span className="text-sm font-normal text-muted-foreground">tap a code to copy it</span></CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 lg:grid-cols-6 gap-4">{kit.colours.map((c) => <ColourCard key={c.hex} c={c} />)}</CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Fonts <span className="text-sm font-normal text-muted-foreground">all free from fonts.google.com, included in the ZIP</span></CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          {FONTS.map(([name, use, fam, w, sample]) => (
            <div key={name} className="py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6" data-testid={`brand-font-${fam.replace(/ /g, "-").toLowerCase()}`}>
              <div className="sm:w-48 shrink-0"><p className="text-sm font-semibold">{name}</p><p className="text-xs text-muted-foreground">{use}</p></div>
              <p className="text-xl sm:text-2xl truncate" style={{ fontFamily: `'${fam}'`, fontWeight: w, color: fam === "Montserrat" ? "#5D6D7E" : undefined }}>{sample}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
